import clsx from 'clsx';
import type {
  SignalFieldMappings,
  SignalVariant,
} from '@arcanum-hq/shared-types';
import { PropertyVisualGrid } from './PropertyVisualGrid';
import type { SnapshotEntry } from './SnapshotBox';
import type { UnitMixRow } from './UnitMixBar';
import {
  useSignalFieldResolver,
  type SignalFieldResolverContext,
} from '@/hooks/use-signal-field-resolver';
import { excelSerialToDate, formatCompactCurrency } from '@/lib/format';

// deal-signal-overhaul Phase 4 Session 2 (2026-05-12).
//
// Zone 3 of the Signal page — 3-column property visual context grid (satellite
// map + snapshot box + unit-mix bar). Thin parent wrapper that resolves the
// per-variant prop arrays via `useSignalFieldResolver` and passes them to the
// already-shipped `<PropertyVisualGrid>` layout primitive.
//
// Variant snapshot key sets per Phase-1 sidecar JSON (verified 2026-05-12):
//   - operating-asset: Property Class · Address · Units · Year 1 NOI ·
//                       Acquisition Date · Total Capitalization
//   - new-development: Product Type · Location · Units · Total Dev Cost ·
//                       Project Start
//
// Unit-mix: no `unitMix*` keys are populated in either sidecar today (per
// Phase 4 blueprint § "<UnitMixBar> — current data source NOT in
// signalFieldMappings"). When all keys resolve to null/0, the existing
// `<UnitMixBar>` empty state renders "No unit-mix data" — graceful
// degradation pending Phase 5's optional sidecar extension.

type SnapshotFormat = 'string' | 'currency' | 'integer' | 'date';

interface SnapshotKeyConfig {
  key: keyof SignalFieldMappings;
  label: string;
  format: SnapshotFormat;
}

const SNAPSHOT_KEYS_BY_VARIANT: Record<SignalVariant, ReadonlyArray<SnapshotKeyConfig>> = {
  'operating-asset': [
    { key: 'snapshotVintageClass', label: 'Property Class', format: 'string' },
    { key: 'snapshotAddress', label: 'Location', format: 'string' },
    { key: 'snapshotUnits', label: 'Units', format: 'integer' },
    { key: 'snapshotYear1NOI', label: 'Year 1 NOI', format: 'currency' },
    { key: 'snapshotAcquisitionDate', label: 'Acquisition Date', format: 'date' },
    {
      key: 'snapshotTotalCapitalization',
      label: 'Total Capitalization',
      format: 'currency',
    },
  ],
  'new-development': [
    { key: 'snapshotProductType', label: 'Product Type', format: 'string' },
    { key: 'snapshotLocation', label: 'Location', format: 'string' },
    { key: 'snapshotUnits', label: 'Units', format: 'integer' },
    {
      key: 'snapshotTotalDevelopmentCost',
      label: 'Total Dev Cost',
      format: 'currency',
    },
    { key: 'snapshotProjectStart', label: 'Project Start', format: 'date' },
  ],
};

interface UnitMixKeyConfig {
  key: keyof SignalFieldMappings;
  label: string;
  /** Tailwind background token — matches the legacy `<SignalOperatingAsset>` palette. */
  color: string;
}

// Same color palette as the pre-Phase-4 `UNIT_MIX_INPUTS` constant in
// `SignalOperatingAsset.tsx` so the visual carries across the transition.
const UNIT_MIX_KEYS: ReadonlyArray<UnitMixKeyConfig> = [
  { key: 'unitMixStudio', label: 'Studio', color: 'bg-text-muted' },
  { key: 'unitMix1Bed', label: '1 Bed', color: 'bg-blue' },
  { key: 'unitMix2Bed', label: '2 Bed', color: 'bg-accent' },
  { key: 'unitMix3Bed', label: '3 Bed', color: 'bg-purple' },
];

function formatScalar(raw: unknown, format: SnapshotFormat): string {
  if (raw == null || raw === '') return '—';
  if (format === 'currency' && typeof raw === 'number' && Number.isFinite(raw)) {
    return formatCompactCurrency(raw);
  }
  if (format === 'integer' && typeof raw === 'number' && Number.isFinite(raw)) {
    return raw.toLocaleString('en-US', { maximumFractionDigits: 0 });
  }
  if (format === 'date' && typeof raw === 'number' && Number.isFinite(raw)) {
    return excelSerialToDate(raw);
  }
  return String(raw);
}

function composeSnapshotEntries(
  variant: SignalVariant,
  resolveFn: (key: keyof SignalFieldMappings) => unknown,
): SnapshotEntry[] {
  return SNAPSHOT_KEYS_BY_VARIANT[variant].map((cfg) => ({
    label: cfg.label,
    value: formatScalar(resolveFn(cfg.key), cfg.format),
  }));
}

function composeUnitMixRows(
  resolveFn: (key: keyof SignalFieldMappings) => unknown,
): UnitMixRow[] {
  const rows: UnitMixRow[] = [];
  for (const cfg of UNIT_MIX_KEYS) {
    const raw = resolveFn(cfg.key);
    if (typeof raw === 'number' && Number.isFinite(raw) && raw > 0) {
      rows.push({ label: cfg.label, count: raw, color: cfg.color });
    }
  }
  return rows;
}

export interface PropertyVisualContextProps {
  /** Auth or public resolver context — same shape `<HeroReturns>` consumes. */
  context: SignalFieldResolverContext;
  variant: SignalVariant;
  /**
   * Aerial coordinates — passed through to `<PropertyMap>` unchanged. Caller
   * resolves from `agent_state.aerialLatLng` (auth) or `publicDeal.aerialLatLng`
   * (public). When either is `null`/`undefined`, the map renders its
   * no-coordinates empty state and the rest of the grid degrades gracefully.
   */
  propertyLat?: number | null;
  propertyLng?: number | null;
  className?: string;
}

/**
 * Resolver-aware parent for `<PropertyVisualGrid>`. Walks variant-specific
 * snapshot + unit-mix keys, formats scalars, hands the pre-composed prop
 * arrays to the layout primitive. Tests can mock `useSignalFieldResolver`
 * to verify the composition without going through the data hooks.
 */
export function PropertyVisualContext({
  context,
  variant,
  propertyLat,
  propertyLng,
  className,
}: PropertyVisualContextProps) {
  const resolver = useSignalFieldResolver(context);

  const snapshotEntries = composeSnapshotEntries(variant, (key) => resolver.resolve(key));
  const unitMixRows = composeUnitMixRows((key) => resolver.resolve(key));

  return (
    <div
      data-testid="property-visual-context"
      data-variant={variant}
      data-has-coords={
        typeof propertyLat === 'number' &&
        Number.isFinite(propertyLat) &&
        typeof propertyLng === 'number' &&
        Number.isFinite(propertyLng)
          ? 'true'
          : 'false'
      }
      className={clsx(className)}
    >
      <PropertyVisualGrid
        satellite={{ lat: propertyLat ?? null, lng: propertyLng ?? null }}
        snapshotEntries={snapshotEntries}
        unitMixRows={unitMixRows}
      />
    </div>
  );
}
